import React, { createContext, useState, useContext, useCallback } from "react";

export type ToastType = "success" | "error" | "info";

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type: ToastType) => void;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType) => {
      setToasts((currentToasts) => {
        // Check for duplicate toasts
        const isDuplicate = currentToasts.some(
          (toast) => toast.message === message && toast.type === type,
        );

        if (isDuplicate) {
          return currentToasts;
        }

        // If this is a success/error toast, remove any existing loading/info toasts
        // to prevent intrusive overlapping (e.g., "Creating..." followed by "Created!")
        let filteredToasts = currentToasts;
        if (type === "success" || type === "error") {
          filteredToasts = currentToasts.filter((toast) => {
            const isLoadingToast =
              toast.type === "info" &&
              (toast.message.includes("Creating") ||
                toast.message.includes("Loading") ||
                toast.message.includes("..."));
            return !isLoadingToast;
          });
        }

        const id = Date.now();
        const newToast = { id, message, type };

        // Auto-dismiss after 3 seconds for less intrusiveness
        setTimeout(() => {
          removeToast(id);
        }, 3000);

        return [...filteredToasts, newToast];
      });
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
